import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import moment from 'moment';
import { rgba } from 'polished';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';

import { CycleMoment } from '@treams/treams-analytics';
import { useLocalize } from '@treams/treams-hooks';
import { CalendarIcon } from '@treams/treams-icons';
import { CycleTemplateInterval } from '@treams/treams-network';
import {
	Color,
	DataGrid,
	DataGridHeaderParams,
	IconColor,
	InfoTip,
	Pill,
	tokens,
} from '@treams/treams-ui';

import { ratio } from '../CycleAnalytics';
import { CycleMomentDistributionChart, Legend } from './CycleMomentDistributionChart';
import { GridProgressCell } from './GridProgressCell';
import { MomentIcon } from './MomentIcon';
import { CycleColumn, buildMomentColumns } from './cycleColumns';

const MomentIconContainer = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 16px;
	height: 16px;

	svg {
		display: block;
		width: 16px;
		height: 16px;
	}
`;

function MomentNameCell({ data }: CustomCellRendererProps<CycleMoment>) {
	const { localize } = useLocalize();
	const { t } = useTranslation();

	if (!data) {
		return null;
	}

	const isActive =
		moment(data.startDate).isBefore(moment()) && moment(data.endDate).isAfter(moment());

	return (
		<Flex alignItems="center" height="100%">
			<MomentIconContainer mr={`${tokens.gap.list}px`}>
				<IconColor color={tokens.icon.secondary}>
					<MomentIcon momentType={data.type} size={16} />
				</IconColor>
			</MomentIconContainer>
			<>{localize(data.title).text}</>
			{isActive && (
				<Box ml={2}>
					<Pill small tint="blue">
						{t('analytics.cycles.moments.active')}
					</Pill>
				</Box>
			)}
		</Flex>
	);
}

function ParticipationCell({ data }: CustomCellRendererProps<CycleMoment>) {
	const { t } = useTranslation();
	const theme = useTheme();

	if (!data) {
		return null;
	}

	const { progress } = data;

	if (!progress) {
		return <Color color="lightBlueDark">{t('analytics.cycles.moments.notStarted')}</Color>;
	}

	const segments = [
		{
			label: t('analytics.cycles.moments.distribution.notStarted'),
			value: Math.max(progress.total - progress.started, 0),
			color: theme.colors.primaryLight,
		},
		{
			label: t('analytics.cycles.moments.distribution.started'),
			value: Math.max(progress.started - progress.completed, 0),
			color: rgba(theme.colors.primary, 0.5),
		},
		{
			label: t('analytics.cycles.moments.distribution.completed'),
			value: progress.completed,
			color: theme.colors.primary,
		},
	];

	return (
		<Flex alignItems="center" height="100%" flex={1}>
			<CycleMomentDistributionChart segments={segments} total={progress.total} />
		</Flex>
	);
}

export function CycleMomentsGrid({
	moments,
	interval,
}: {
	moments: CycleMoment[];
	interval: CycleTemplateInterval | undefined;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const theme = useTheme();

	const columnDefs = useMemo<ColDef<CycleMoment>[]>(() => {
		// Renderers and layout that only apply to the grid, keyed by shared column id.
		const gridColumnLayout: Record<string, ColDef<CycleMoment>> = {
			moment: { flex: 2, cellRenderer: MomentNameCell },
			employeeCompleted: {
				headerComponentParams: {
					extra: (
						<InfoTip content={t('analytics.cycles.moments.table.info.employeeCompleted')} portal />
					),
				} satisfies Partial<DataGridHeaderParams>,
			},
			participation: {
				flex: 3,
				cellRenderer: ParticipationCell,
				headerComponentParams: {
					extra: (
						<Legend
							align="end"
							fitContent
							segments={[
								{
									label: t('analytics.cycles.progress.total'),
									color: theme.colors.primaryLight,
								},
								{
									label: t('analytics.cycles.progress.started'),
									color: rgba(theme.colors.primary, 0.5),
								},
								{
									label: t('analytics.cycles.progress.completed'),
									color: theme.colors.primary,
								},
							]}
						/>
					),
				} satisfies Partial<DataGridHeaderParams>,
			},
		};

		const toColDef = (column: CycleColumn<CycleMoment>): ColDef<CycleMoment> => {
			if (column.type === 'fraction') {
				return {
					colId: column.id,
					headerName: column.header,
					minWidth: 130,
					valueGetter: ({ data }: ValueGetterParams<CycleMoment>) => {
						const fraction = data ? column.value(data) : null;

						return fraction ? ratio(fraction.value, fraction.total) : null;
					},
					cellRenderer: ({ data }: CustomCellRendererProps<CycleMoment>) => {
						const fraction = data ? column.value(data) : null;

						return fraction ? (
							<GridProgressCell value={fraction.value} total={fraction.total} />
						) : (
							<Color color="lightBlueDark">{column.emptyText}</Color>
						);
					},
					...gridColumnLayout[column.id],
				};
			}

			const { sortValue } = column;

			return {
				colId: column.id,
				headerName: column.header,
				// Sort on the numeric value where the visible text doesn't sort
				// naturally (dates, day counts), but always display the text.
				valueGetter: ({ data }: ValueGetterParams<CycleMoment>) =>
					data ? (sortValue ? sortValue(data) : column.value(data)) : null,
				cellRenderer: ({ data }: CustomCellRendererProps<CycleMoment>) =>
					data ? column.value(data) : null,
				...gridColumnLayout[column.id],
			};
		};

		return buildMomentColumns({ interval, t, localize }).map(toColDef);
	}, [interval, t, localize, theme]);

	return (
		<DataGrid<CycleMoment>
			rowData={moments}
			columnDefs={columnDefs}
			defaultColDef={{ sortable: true }}
			emptyState={{
				icon: <CalendarIcon />,
				title: t('analytics.cycles.moments.empty.title'),
				description: t('analytics.cycles.moments.empty.description'),
			}}
		/>
	);
}
